/* eslint-disable */
import {
  Alert,
  Anchor,
  Button,
  CloseButton,
  Divider,
  Group,
  PasswordInput,
  Text,
  TextInput,
} from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import axios from "axios";
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ExclamationMark } from "tabler-icons-react";
import { z } from "zod";

import { useAuth } from "api/auth";

import { LoginFormValues } from "../types";
import AuthLayout from "./AuthLayout";
import { useGetUserPermissions } from "api/permission/getpermission";

const loginSchema = z.object({
  email: z
    .string()
    .email({ message: "Invalid email address" })
    .min(1, { message: "Please enter your email address" }),
  password: z
    .string()
    .min(6, { message: "Password must contain at least 6 characters" })
    .min(1, { message: "Please enter your password" }),
});

export function LoginForm() {
  const { login, isLoggingIn, loginAsGuest, isLoggingInAsGuest } = useAuth();

  const navigate = useNavigate();
  const location = useLocation();
  const form = useForm<LoginFormValues>({
    initialValues: {
      email: "",
      password: "",
    },
    validate: zodResolver(loginSchema),
  });
  const [showFormError, setShowFormError] = useState(false);

  const handleSubmit = (values: LoginFormValues) => {
    login(values, {
      onSuccess: () => {
        const state = location.state as { path: string };
        navigate(state?.path || "/attendance-dashboard", { replace: true });
      },

      onError: (err) => {
        if (axios.isAxiosError(err)) {
          const status = err.response?.status;
          const apiMessage =
            err.response?.data?.message || "Invalid email or password";

          if (status === 401) {
            setShowFormError(apiMessage);
            return;
          }

          showNotification({
            title: "Error",
            message: apiMessage,
            color: "red",
            icon: <ExclamationMark />,
          });
        } else {
          showNotification({
            title: "Server Error",
            message: "Please try again later",
            color: "red",
          });
        }
      },
    });
  };

  const handleLoginAsGuest = () => {
    loginAsGuest(undefined, {
      onSuccess: () => {
        navigate("/attendance-dashboard", { replace: true });
      },
      onError: () => {
        showNotification({
          title: "Server Error",
          message: "Please try again later",
          color: "red",
          icon: <ExclamationMark />,
        });
      },
    });
  };

  return (
    <AuthLayout title="Sign In">
      {/* <Button
        size="md"
        color="violet.6"
        variant="outline"
        onClick={handleLoginAsGuest}
        loading={isLoggingInAsGuest}
        disabled
      >
        Quick Demo
      </Button> */}
      <Text color="dark.3" size={14}>
        * An account is required
      </Text>
      <Divider
        my={20}
        label={
          <Text size={14} color="dark.3">
            or
          </Text>
        }
        labelPosition="center"
      />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        {showFormError && (
          <Alert
            color="red"
            icon={<AlertCircle color="#E03131" />}
            mb={20}
            styles={{
              wrapper: {
                alignItems: "center",
              },
            }}
          >
            <Group noWrap>
              <Text color="red.9">Incorrect email or password</Text>
              <CloseButton
                color="red.9"
                ml="auto"
                aria-label="Close alert"
                onClick={() => setShowFormError(false)}
              />
            </Group>
          </Alert>
        )}
        <TextInput label="Email" mb={20} {...form.getInputProps("email")} />
        <PasswordInput label="Password" {...form.getInputProps("password")} />
        <Button
          type="submit"
          loading={isLoggingIn}
          w="100%"
          mt={30}
          size="md"
          color="violet.5"
        >
          Sign In
        </Button>
      </form>
      <Divider my={30} />
      {/* <Text color="gray.7" align="center">
        Don&apos;t have an account?
      </Text> */}
      {/* <Anchor
        component={Link}
        to="/register"
        sx={{ display: "block" }}
        align="center"
      >
        Sign up
      </Anchor> */}
      <Anchor
        component={Link}
        to="/privacy-policy"
        sx={{ display: "block" }}
        align="center"
      >
        Privacy Policies
      </Anchor>
    </AuthLayout>
  );
}
